/** Watch a dispatched entity-export task and offer its artifact for download.
 *
 * The console has no per-task callback plumbing (BgTaskBell only polls the
 * list), so this is a detached polling loop: it survives the export dialog
 * closing because nothing here is tied to a component lifecycle. When the task
 * completes, a long-lived snackbar offers the artifact; the presigned URL is
 * fetched AT CLICK TIME (it expires in an hour, and the operator may click
 * late), exactly like TaskDetailsDialog's ArtifactRow. Whatever happens, the
 * file also stays available under Background Tasks.
 */
import {
  artifactUrl,
  isActiveState,
  result as fetchResult,
  retrieve,
  TASK_STATE,
} from '@/api/bgtasks';
import type { SnackbarShowOptions } from '@/components/Snackbar';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';

interface SnackbarLike {
  show: (
    message: string,
    severity?: 'success' | 'error' | 'info' | 'warning',
    opts?: SnackbarShowOptions,
  ) => void;
}

const POLL_MS = 4_000;
/** Stop watching after this long; the artifact is still in Background Tasks. */
const MAX_WATCH_MS = 30 * 60 * 1_000;
/** Tolerate this many consecutive poll failures before giving up quietly. */
const MAX_CONSECUTIVE_ERRORS = 5;
const OFFER_MS = 20_000;

export function watchExportTask(taskId: string, snackbar: SnackbarLike): void {
  const startedAt = Date.now();
  let consecutiveErrors = 0;

  const poll = async (): Promise<void> => {
    if (Date.now() - startedAt > MAX_WATCH_MS) return;
    try {
      const task = await retrieve(taskId);
      consecutiveErrors = 0;
      if (isActiveState(task.state)) {
        window.setTimeout(poll, POLL_MS);
        return;
      }
      if (task.state === TASK_STATE.CANCELLED) {
        snackbar.show('Export cancelled.', 'info');
        return;
      }
      const viewTask = { label: 'View task', onClick: () => openBackgroundTask(taskId) };
      if (task.state !== TASK_STATE.COMPLETED) {
        snackbar.show(task.error_message || 'Export failed.', 'error', {
          autoHideDuration: OFFER_MS,
          action: viewTask,
        });
        return;
      }
      // Warnings the result may carry: duplicate external IDs (re-import is
      // ambiguous) and multi-tenant operators skipped for lack of a default
      // import key. Full detail lives on the task's result panel.
      let notes = '';
      try {
        const { result } = await fetchResult(taskId);
        const dup = result?.duplicates as { group_count?: number } | undefined;
        if (dup?.group_count) {
          notes +=
            ` — ${dup.group_count} duplicated external ID(s); re-importing is ` +
            'ambiguous for those records. See the task details for the list.';
        }
        const skipped = result?.skipped_tenants as unknown[] | undefined;
        if (skipped?.length) {
          notes += ` — ${skipped.length} operator(s) skipped (no default import key); see the task log.`;
        }
      } catch {
        // non-fatal: the result panel under Background Tasks still shows it
      }
      const artifacts = task.artifacts ?? [];
      if (artifacts.length === 0) {
        snackbar.show(`Export finished but produced no file.${notes}`, 'warning', {
          action: viewTask,
        });
        return;
      }
      if (artifacts.length > 1) {
        // per-operator files: no single link to offer — the task's details
        // dialog lists every artifact with its own download.
        snackbar.show(
          `Export ready — ${artifacts.length} files (one per operator).${notes}`,
          notes ? 'warning' : 'success',
          { autoHideDuration: OFFER_MS, action: viewTask },
        );
        return;
      }
      const artifact = artifacts[0]!;
      snackbar.show(`Export ready (${artifact.name})${notes}`, notes ? 'warning' : 'success', {
        autoHideDuration: OFFER_MS,
        actions: [
          {
            label: 'Download',
            onClick: () => {
              artifactUrl(taskId, artifact.name)
                .then((r) => window.open(r.url, '_blank', 'noopener'))
                .catch(() =>
                  snackbar.show(
                    'Could not fetch the download link — it is still available under Background Tasks.',
                    'warning',
                  ),
                );
            },
          },
          viewTask,
        ],
      });
    } catch {
      consecutiveErrors += 1;
      if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
        window.setTimeout(poll, POLL_MS * 2);
      }
      // else: give up quietly — Background Tasks still shows the outcome.
    }
  };

  window.setTimeout(poll, POLL_MS);
}
